import type {
  CollabEnvelope,
  PluginNamespace
} from '../types/envelope.js';
import {
  CURRENT_PROTOCOL_VERSION
} from '../types/envelope.js';
import type {
  MeshRoutingHeaders
} from '../types/routing.js';
import {
  SecurityTier
} from '../types/security.js';
import {
  parseHnsUri,
  parseHtsUri
} from './uri-validation.js';
import {
  parseEventId
} from './uuid-validation.js';

const PLUGIN_NAMESPACE_REGEX = /^[a-z0-9_-]+(?:[.\/][a-z0-9_-]+)*$/;

function isRecord(
  value: unknown
): value is Record<string, unknown> {
  return typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value);
}

function isSecurityTier(
  value: unknown
): value is SecurityTier {
  for (const tier of Object.values(SecurityTier)) {
    if (tier === value) return true;
  }

  return false;
}

function parsePluginNamespaceValue(
  value: unknown
): PluginNamespace {
  if (typeof value !== 'string') {
    throw new Error(
      'Invalid PluginNamespace: must be a string'
    );
  }

  const normalized = value.trim().toLowerCase();

  if (!PLUGIN_NAMESPACE_REGEX.test(normalized)) {
    throw new Error(
      'Invalid PluginNamespace'
    );
  }

  return normalized as PluginNamespace;
}

function parseRouting(
  value: unknown,
  createdAt: number
): MeshRoutingHeaders | undefined {
  if (value === undefined) return undefined;

  if (!isRecord(value)) {
    throw new Error(
      'Invalid Envelope: malformed routing headers'
    );
  }

  const hopLimit = value.hopLimit;
  const expiresAt = value.expiresAt;

  if (
    typeof hopLimit !== 'number' ||
    !Number.isInteger(hopLimit) ||
    hopLimit < 0
  ) {
    throw new Error(
      'Invalid Envelope: invalid hopLimit'
    );
  }

  if (
    typeof expiresAt !== 'number' ||
    !Number.isFinite(expiresAt) ||
    expiresAt <= createdAt
  ) {
    throw new Error(
      'Invalid Envelope: invalid expiresAt'
    );
  }

  const routing: MeshRoutingHeaders = {
    hopLimit,
    expiresAt
  };

  if (value.routeId !== undefined) {
    if (
      typeof value.routeId !== 'string' ||
      value.routeId.length === 0
    ) {
      throw new Error(
        'Invalid Envelope: invalid routeId'
      );
    }

    routing.routeId = value.routeId;
  }

  if (value.traceId !== undefined) {
    if (
      typeof value.traceId !== 'string' ||
      value.traceId.length === 0
    ) {
      throw new Error(
        'Invalid Envelope: invalid traceId'
      );
    }

    routing.traceId = value.traceId;
  }

  if (value.fragmentation !== undefined) {
    if (!isRecord(value.fragmentation)) {
      throw new Error(
        'Invalid Envelope: malformed fragmentation'
      );
    }

    const fragmentId = value.fragmentation.fragmentId;
    const index = value.fragmentation.index;
    const count = value.fragmentation.count;

    if (
      typeof fragmentId !== 'string' ||
      fragmentId.length === 0 ||
      typeof index !== 'number' ||
      !Number.isInteger(index) ||
      typeof count !== 'number' ||
      !Number.isInteger(count) ||
      count <= 0 ||
      index < 0 ||
      index >= count
    ) {
      throw new Error(
        'Invalid Envelope: invalid fragmentation'
      );
    }

    routing.fragmentation = {
      fragmentId,
      index,
      count
    };
  }

  return routing;
}

export function parsePluginNamespace(
  value: unknown
): PluginNamespace {
  return parsePluginNamespaceValue(value);
}

export function validateEnvelope(
  raw: unknown
): CollabEnvelope<unknown> {
  if (!isRecord(raw)) {
    throw new Error(
      'Invalid Envelope: must be an object'
    );
  }

  if (
    raw.protocolVersion !== CURRENT_PROTOCOL_VERSION
  ) {
    throw new Error(
      'Invalid Envelope: protocolVersion mismatch'
    );
  }

  if (!('payload' in raw)) {
    throw new Error(
      'Invalid Envelope: missing payload'
    );
  }

  if (
    typeof raw.senderId !== 'string' ||
    raw.senderId.trim().length === 0
  ) {
    throw new Error(
      'Invalid Envelope: invalid sender ID'
    );
  }

  if (
    typeof raw.createdAt !== 'number' ||
    !Number.isFinite(raw.createdAt) ||
    raw.createdAt < 0
  ) {
    throw new Error(
      'Invalid Envelope: invalid timestamp'
    );
  }

  if (!isSecurityTier(raw.securityTier)) {
    throw new Error(
      'Invalid Envelope: unknown securityTier'
    );
  }

  const eventId = parseEventId(raw.eventId);
  const sourceUri = parseHnsUri(raw.sourceUri);
  const destinationUri =
    parseHtsUri(raw.destinationUri).canonical;
  const pluginId =
    parsePluginNamespaceValue(raw.pluginId);
  const routing =
    parseRouting(raw.routing, raw.createdAt);

  const envelope: CollabEnvelope<unknown> = {
    protocolVersion: CURRENT_PROTOCOL_VERSION,
    eventId,
    sourceUri,
    destinationUri,
    pluginId,
    senderId: raw.senderId,
    createdAt: raw.createdAt,
    securityTier: raw.securityTier,
    payload: raw.payload
  };

  if (typeof raw.sessionId === 'string') {
    if (raw.sessionId.length === 0) {
      throw new Error(
        'Invalid Envelope: sessionId cannot be empty'
      );
    }

    envelope.sessionId = raw.sessionId;
  } else if (raw.sessionId !== undefined) {
    throw new Error(
      'Invalid Envelope: sessionId must be a string'
    );
  }

  if (routing !== undefined) {
    envelope.routing = routing;
  }

  if (raw.signature !== undefined) {
    if (
      typeof raw.signature !== 'string' ||
      raw.signature.length === 0
    ) {
      throw new Error(
        'Invalid Envelope: invalid signature'
      );
    }

    envelope.signature = raw.signature;
  }

  return envelope;
}
